/**
 * Fixes de indexación GSC (oct 2026) — redirects 301 y señales noindex en edge.
 * Sincronizar: node backend/scripts/sync-gsc-coverage-firebase.js
 */

'use strict';

const {
  VALID_CITIES_EN_FULL,
  VALID_CITIES_UK,
  VALID_CITIES_US,
  UK_CITY_SLUGS,
  US_CITY_SLUGS,
} = require('./locationRegistry.cjs');

/**
 * Barrios / slugs históricos que apuntaban a /findyourcoliving/{slug} pero no son
 * rutas válidas en el router → consolidar en borough/ciudad padre.
 */
const FINDYOURCOLIVING_ORPHAN_TO_CANONICAL = {
  bushwick: 'brooklyn',
  ridgewood: 'brooklyn',
  dumbo: 'brooklyn',
  'park-slope': 'brooklyn',
  'crown-heights': 'brooklyn',
  'bedford-stuyvesant': 'brooklyn',
  'hells-kitchen': 'manhattan',
  'midtown-west': 'manhattan',
  'midtown-manhattan': 'manhattan',
  'midtown-east': 'manhattan',
  'lower-east-side': 'manhattan',
  'east-village': 'manhattan',
  'upper-east-side': 'manhattan',
  'upper-west-side': 'manhattan',
  'greenwich-village': 'manhattan',
  'gramercy-park': 'manhattan',
  'west-harlem': 'manhattan',
  'clinton-hill': 'brooklyn',
  greenpoint: 'brooklyn',
  'downtown-jersey-city': 'jersey-city',
};

/** Slugs UK/US indexables en EN pero no en lista ES → 301 desde /encuentratucoliving/ */
function getUkUsCityOnSpanishPathRedirects() {
  const esAllowed = new Set([
    ...require('./locationRegistry.cjs').VALID_CITIES_ES_SEARCH,
  ]);
  const redirects = [];

  for (const slug of [...UK_CITY_SLUGS, ...US_CITY_SLUGS]) {
    if (esAllowed.has(slug)) continue;
    redirects.push({
      source: `/encuentratucoliving/${slug}`,
      destination: `/findyourcoliving/${slug}`,
      type: 301,
    });
  }

  return redirects;
}

function getFindYourColivingOrphanRedirects() {
  return Object.entries(FINDYOURCOLIVING_ORPHAN_TO_CANONICAL).map(([slug, target]) => ({
    source: `/findyourcoliving/${slug}`,
    destination: `/findyourcoliving/${target}`,
    type: 301,
  }));
}

/**
 * URLs legacy /coliving/coliving-{provider}_{id} → ficha pública /colivings/{provider}/{id}
 */
function getLegacyColivingShowcaseRedirects() {
  const known = [
    { source: '/coliving/coliving-gaiastays_421', destination: '/colivings/gaiastays/gaiastays_421' },
    { source: '/coliving/coliving-gaiastays_420', destination: '/colivings/gaiastays/gaiastays_420' },
  ];
  return known.map((r) => ({ ...r, type: 301 }));
}

/** Guías barrio auto sin redirect aún (informe GSC "página con redirección" / noindex) */
function getExtraNeighborhoodLegacyRedirects() {
  const pairs = [
    ['coliving-ridgewood-new-york', '/findyourcoliving/brooklyn'],
    ['coliving-hells-kitchen-new-york', '/findyourcoliving/manhattan'],
    ['coliving-upper-east-side-new-york', '/findyourcoliving/manhattan'],
    ['coliving-hackney-london', '/best-coliving-hackney-guide-2026'],
  ];
  return pairs.map(([source, destination]) => ({
    source: `/${source}`,
    destination,
    type: 301,
  }));
}

function resolveFindYourColivingMarketplaceSlug(slug) {
  const s = String(slug || '').toLowerCase();
  if (!s) return null;
  if (VALID_CITIES_EN_FULL.includes(s)) return s;
  return FINDYOURCOLIVING_ORPHAN_TO_CANONICAL[s] || null;
}

/** /coliving/:ciudadES → /encuentratucoliving/:ciudad (landing Google Ads) */
function getColivingCityShortcutRedirects() {
  const cities = [
    'madrid', 'barcelona', 'valencia', 'sevilla', 'bilbao', 'malaga',
    'salamanca', 'alicante', 'granada', 'zaragoza', 'las-palmas', 'tenerife', 'pamplona',
  ];
  return cities.map((c) => ({
    source: `/coliving/${c}`,
    destination: `/encuentratucoliving/${c}`,
    type: 301,
  }));
}

function getAllGscCoverageRedirects() {
  const seen = new Set();
  const out = [];

  for (const r of [
    ...getColivingCityShortcutRedirects(),
    ...getUkUsCityOnSpanishPathRedirects(),
    ...getFindYourColivingOrphanRedirects(),
    ...getLegacyColivingShowcaseRedirects(),
    ...getExtraNeighborhoodLegacyRedirects(),
  ]) {
    const key = r.source;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }
  return out;
}

module.exports = {
  FINDYOURCOLIVING_ORPHAN_TO_CANONICAL,
  getColivingCityShortcutRedirects,
  getUkUsCityOnSpanishPathRedirects,
  getFindYourColivingOrphanRedirects,
  getLegacyColivingShowcaseRedirects,
  getExtraNeighborhoodLegacyRedirects,
  resolveFindYourColivingMarketplaceSlug,
  getAllGscCoverageRedirects,
};
